import React, { useState, useLayoutEffect, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks';
import { getUID, CRISTP_ID, addNewStyle, wait } from '@/utils';
import { useGames } from '@/Context/GameContext';
import MenuIcon from '@/components/icons/MenuIcon';
import SearchIcon from '@/components/icons/SearchIcon';
import CasinoIcon from '@/components/icons/CasinoIcon';
import SportsIcon from '@/components/icons/SportsIcon';

import NotificationIcon from '@/components/icons/NotificationIcon';
import styles from './BottomTab.module.css';

/**
 * BottomTab Component
 * Mobile bottom navigation bar
 * Only visible on mobile devices
 * Matches old SkylaBottomTab structure
 */
const SPORTSBOOK_GAME_ID = '4d31f1186a81e208c003a7e37411ce35';

const BottomTab = () => {
  const { isMobile } = useScreenSize();
  const location = useLocation();
  const navigate = useNavigate();
  const { handleJsGameLaunch } = useGames();
  const [loaded, setLoaded] = useState(false);
  const [navItems, setNavItems] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  useLayoutEffect(() => {
    if (!isMobile) return; // Early return inside hook is fine

    const loggedInNavItems = [
      { path: '/menu', icon: <MenuIcon size="24px" />, label: 'Menu' },
      { path: '/modal?mode=search', icon: <SearchIcon size="24px" />, label: 'Search' },
      { path: '/casino', icon: <CasinoIcon size="24px" />, label: 'Casino' },
      { path: '#sportsbook', icon: <SportsIcon size="24px" />, label: 'Sportsbook', isSportsbook: true },
      // { path: '/sports/markets/in-play', icon: <SportsIcon size="24px" />, label: 'Sports' },
      { path: '/modal?mode=notification', icon: <NotificationIcon size="24px" />, label: 'Notification' }
    ];

    const withoutLoginNavItems = [
      { path: '/menu', icon: <MenuIcon size="24px" />, label: 'Menu' },
      { path: '/casino', icon: <CasinoIcon size="24px" />, label: 'Casino' },
      { path: '#sportsbook', icon: <SportsIcon size="24px" />, label: 'Sportsbook', isSportsbook: true },
      // { path: '/sports/markets/in-play', icon: <SportsIcon size="24px" />, label: 'Sports' },
    ];

    const checkAuthStatus = () => {
      const token = localStorage.getItem('token');
      setIsLoggedIn(!!token);
      const items = !!token ? loggedInNavItems : withoutLoginNavItems;
      setNavItems(items);
      const currentItem = items.find(item => 
        item.path === location.pathname || 
        (item.path === '/sports/markets/in-play' && location.pathname.startsWith('/sports'))
      );
      setActiveTab(currentItem?.label || '');
    };

    checkAuthStatus();
    window.addEventListener('storage', checkAuthStatus);
    return () => window.removeEventListener('storage', checkAuthStatus);
  }, [location.pathname, isMobile]);

  const support = () => {
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = CRISTP_ID;

    window.$crisp.push(["config", "container:fullpage", true]);
    window.$crisp.push(["config", "position:reverse", true]);
    window.$crisp.push(["config", "hide:on:mobile", false]);
    window.$crisp.push(["config", "button:hide", true]);

    if (!loaded) {
      setLoaded(true);
      const script = document.createElement('script');
      script.src = "https://client.crisp.chat/l.js";
      script.id = "chtt";
      script.async = true;

      script.onload = function () {
        setTimeout(() => {
          window.$crisp.push(["do", "chat:open"]);
          const crispStyles = `
            .crisp-client .cc-1xry { display: none !important; }
            .crisp-client .cc-kv6t { visibility: visible !important; opacity: 1 !important; display: block !important; }
            .crisp-client .cc-k5k0 { visibility: visible !important; opacity: 1 !important; display: block !important; }
            @media screen and (max-width: 768px) {
              #crisp-chatbox { display: block !important; z-index: 1000000 !important; }
              .crisp-client .cc-kv6t { max-height: 100% !important; height: 100% !important; }
            }
          `;
          const styleSheet = document.createElement("style");
          styleSheet.id = "crisp-custom-styles";
          styleSheet.innerText = crispStyles;
          document.head.appendChild(styleSheet);
        }, 1000);
      };

      document.head.appendChild(script);
    } else {
      window.$crisp.push(["do", "chat:open"]);
    }

    addNewStyle("#crisp-chatbox { display: block !important; }");
    addNewStyle(".crisp-client .cc-1xry { display: none !important; }");

    wait(2000).then(() => {
      const close = document.querySelector(".cc-wdhl");
      if (close) {
        close.addEventListener("click", function () {
          addNewStyle("#crisp-chatbox { display: none !important; }");
          addNewStyle(".crisp-client { display: none !important; visibility: hidden !important; }");
          window.$crisp.push(["do", "chat:hide"]);
          window.$crisp.push(["do", "session:reset"]);
          const crispScript = document.getElementById("chtt");
          if (crispScript) crispScript.remove();
          const customStyles = document.getElementById("crisp-custom-styles");
          if (customStyles) customStyles.remove();
          setLoaded(false);
        });
      }
    });
  };

  const handleSportsBookClick = async () => {
    await handleJsGameLaunch(SPORTSBOOK_GAME_ID, navigate, 'SPORTSBOOK');
  };

  const manageClick = (label, itemOnClick) => {
    setActiveTab(label);
    if (label === 'Chat') {
      support();
    } else if (label === 'Sportsbook') {
      handleSportsBookClick();
    } else if (itemOnClick) {
      itemOnClick();
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const uid = getUID();
      if (!uid) return;
      const response = await fetch(`https://api.jackopot.com/firebase/unread-count/${uid}`);
      const data = await response.json();
      if (data.success) {
        setUnreadCount(data.unreadCount);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  useEffect(() => {
    if (!isMobile) return; // Early return inside hook is fine
    if (isLoggedIn) {
      fetchUnreadCount();
    }
  }, [isLoggedIn, isMobile]);

  // iOS Safari fix - ensure bottom tab stays fixed at viewport bottom
  useEffect(() => {
    if (!isMobile) return;

    const bottomTabElement = document.querySelector(`.${styles.bottomTab}`);
    if (!bottomTabElement) return;

    // Force fixed positioning on iOS Safari
    const ensureFixed = () => {
      bottomTabElement.style.position = 'fixed';
      bottomTabElement.style.bottom = '0';
      bottomTabElement.style.left = '0';
      bottomTabElement.style.right = '0';
      bottomTabElement.style.width = '100%';
    };

    // Initial setup
    ensureFixed();

    // Handle viewport changes (iOS Safari address bar)
    const handleViewportChange = () => {
      requestAnimationFrame(ensureFixed);
    };

    // Listen for events that might affect viewport
    window.addEventListener('resize', handleViewportChange, { passive: true });
    window.addEventListener('orientationchange', handleViewportChange);
    window.addEventListener('scroll', ensureFixed, { passive: true });

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      window.removeEventListener('scroll', ensureFixed);
    };
  }, [isMobile, styles.bottomTab]);

  if (!isMobile) {
    return null; // Only show on mobile
  }

  return (
    <nav className={styles.bottomTab}>
      <div className={styles.bottomTabContainer}>
        {navItems.map((item) => {
          const isActive = activeTab === item.label;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`${styles.bottomTabItem} ${isActive ? styles.active : ''}`}
              onClick={(e) => {
                if (item.label === 'Sportses' || item.label === 'Chat' || item.isSportsbook) {
                  e.preventDefault();
                }
                manageClick(item.label, item.onClick);
              }}
            >
              <div className={`${styles.bottomTabIcon} ${isActive ? styles.active : ''}`}>
                {item.label === 'Notification' && unreadCount > 0 ? (
                  <div className={styles.notificationWrapper}>
                    {item.icon}
                    <span className={styles.notificationBadge}>
                      {unreadCount}
                    </span>
                  </div>
                ) : (
                  item.icon
                )}
              </div>
              <span className={styles.bottomTabLabel}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomTab;
